/**
 * Drawing a tie across a line break (MUS-522).
 *
 * A repertoire song is split into one phrase per line, and each phrase is loaded
 * into OSMD on its own. OSMD only draws a tie it can see both ends of, so a tie
 * that crosses a line break is not drawn at all: the line that ends holds a tie
 * start with no stop (OSMD keeps a one-note tie and skips it), and the next line
 * opens with a tie stop with no start (OSMD drops it, and any tie it runs on into).
 *
 * The engraving convention for a tie across a system break is two half arcs: one
 * leaves the held note toward the end of its line, the other arrives at the
 * continuing note from the start of the next line. OSMD draws exactly that when
 * one document breaks a tie across systems, as two VexFlow StaveTies with one note
 * each. This module reads the arcs OSMD will miss from the phrase's MusicXML and
 * adds them to the laid-out measures the same way, so OSMD draws them with its own
 * ties: same curve, thickness, colour and theme, moving with the music.
 */
import Vex from 'vexflow';
import {
  GraphicalMusicSheet,
  GraphicalNote,
  VexFlowGraphicalNote,
  VexFlowMeasure,
} from '@museflow-ai/opensheetmusicdisplay';
import {
  childElements,
  firstChild,
  tieTypesOf,
  timedNotesOfPart,
  TimedNote,
} from './crossPhraseTies';
import VF = Vex.Flow;

/** A tied note, as written: which staff, which pitch, and when. */
export type TieArcNote = {
  /** The staff counted across the whole document, from 0 (OSMD's staff id). */
  staffIndex: number;
  /** MIDI pitch as written (before any transposition). */
  midiPitch: number;
  /** Onset from the phrase's start, in whole notes. */
  onset: number;
};

/** Which way the arc curves; undefined leaves it to the stem, as for a tie within a line. */
export type TieArcDirection = 'above' | 'below' | undefined;

/**
 * An arc OSMD does not draw for a phrase:
 * - toLineEnd: the first half of a tie that runs on into the next line.
 * - fromLineStart: the second half of a tie that began on the line before.
 * - withinLine: a tie in this line that continues one from the line before
 *   (OSMD drops the whole run once it has dropped its opening stop).
 */
export type LineBreakTieArc =
  | { kind: 'toLineEnd'; from: TieArcNote; direction: TieArcDirection }
  | { kind: 'fromLineStart'; to: TieArcNote; direction: TieArcDirection }
  | { kind: 'withinLine'; from: TieArcNote; to: TieArcNote; direction: TieArcDirection };

const ONSET_TOLERANCE = 1e-4;
/** OSMD puts the ties of second voices (2 in the upper staff, 6 in the lower) below. */
const SECOND_VOICES = new Set(['2', '6']);

const arcNoteOf = (note: TimedNote): TieArcNote => ({
  staffIndex: note.staffIndex,
  midiPitch: note.midiPitch,
  onset: note.onset,
});

/** The placement a <tied> (or <tie>) of this type asks for, if any. */
function writtenPlacement(note: Element, tieType: 'start' | 'stop'): TieArcDirection {
  const tieElements = [
    ...childElements(note, 'tie'),
    ...childElements(note, 'notations').flatMap((notations) => childElements(notations, 'tied')),
  ].filter((element) => {
    const type = element.getAttribute('type');
    return type === tieType || type === 'continue';
  });
  const placements = tieElements.map((element) => {
    const orientation = element.getAttribute('orientation');
    const placement = element.getAttribute('placement');
    if (orientation === 'over' || placement === 'above') return 'above';
    if (orientation === 'under' || placement === 'below') return 'below';
    return undefined;
  });
  return placements.find((placement) => placement !== undefined);
}

/**
 * The direction OSMD would give this tie within one document: the placement the
 * MusicXML writes; else, among several ties leaving the same chord, the highest
 * above and the rest below; else below for a second voice; else by the stem.
 */
function arcDirection(
  anchor: TimedNote,
  tieType: 'start' | 'stop',
  tiesAtTheSameChord: TimedNote[],
): TieArcDirection {
  const written = writtenPlacement(anchor.element, tieType);
  if (written) return written;
  const chordTies = tiesAtTheSameChord.filter(
    (note) =>
      note.staffIndex === anchor.staffIndex &&
      Math.abs(note.onset - anchor.onset) < ONSET_TOLERANCE,
  );
  if (chordTies.length > 1) {
    const highestPitch = Math.max(...chordTies.map((note) => note.midiPitch));
    return anchor.midiPitch === highestPitch ? 'above' : 'below';
  }
  return SECOND_VOICES.has(anchor.voice) ? 'below' : undefined;
}

type OpenTie = { origin: 'thisPhrase' | 'earlierPhrase'; lastNote: TimedNote };

type ArcDraft =
  | { kind: 'toLineEnd'; from: TimedNote }
  | { kind: 'fromLineStart'; to: TimedNote }
  | { kind: 'withinLine'; from: TimedNote; to: TimedNote };

/** The arcs OSMD will not draw for one part's notes, before their directions. */
function arcDraftsOfPart(notes: TimedNote[]): ArcDraft[] {
  const { drafts, openTies } = notes.reduce<{ drafts: ArcDraft[]; openTies: Map<string, OpenTie> }>(
    (state, note) => {
      const types = tieTypesOf(note.element);
      const open = state.openTies.get(note.lineKey);
      const openTies = new Map(state.openTies);
      const stopDrafts: ArcDraft[] = [];
      if (types.has('stop')) {
        if (open === undefined) stopDrafts.push({ kind: 'fromLineStart', to: note });
        if (open?.origin === 'earlierPhrase') {
          stopDrafts.push({ kind: 'withinLine', from: open.lastNote, to: note });
        }
        openTies.delete(note.lineKey);
      }
      if (types.has('start')) {
        const continuesEarlierTie =
          types.has('stop') && (open === undefined || open.origin === 'earlierPhrase');
        openTies.set(note.lineKey, {
          origin: continuesEarlierTie ? 'earlierPhrase' : 'thisPhrase',
          lastNote: note,
        });
      }
      return { drafts: [...state.drafts, ...stopDrafts], openTies };
    },
    { drafts: [], openTies: new Map() },
  );
  const toLineEnd: ArcDraft[] = Array.from(openTies.values()).map((open) => ({
    kind: 'toLineEnd',
    from: open.lastNote,
  }));
  return [...drafts, ...toLineEnd];
}

/** How many staves a part has (its <staves>, 1 when it does not say). */
function staffCountOf(part: Element): number {
  const staves = childElements(part, 'measure')
    .flatMap((measure) => childElements(measure, 'attributes'))
    .map((attributes) => Number(firstChild(attributes, 'staves')?.textContent))
    .find((count) => Number.isFinite(count) && count > 0);
  return staves ?? 1;
}

/**
 * The tie arcs OSMD will not draw for a phrase, read from its MusicXML: the first
 * half of each tie that runs on past the line's end, the second half of each tie
 * that began on the line before, and the ties within the line that continue one.
 * Empty when the document cannot be read or has no tie across a line break.
 */
export function readLineBreakTieArcs(musicXml: string): LineBreakTieArc[] {
  if (typeof DOMParser === 'undefined' || !musicXml) return [];
  const document = new DOMParser().parseFromString(musicXml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length > 0) return [];

  const parts = Array.from(document.getElementsByTagName('part'));
  const staffOffsets = parts.reduce<number[]>(
    (offsets, part, index) => [...offsets, offsets[index] + staffCountOf(part)],
    [0],
  );
  const notesByPart = parts.map((part, index) => timedNotesOfPart(part, staffOffsets[index]));
  const notes = notesByPart.flat();
  const drafts = notesByPart.flatMap(arcDraftsOfPart);
  const tieStarts = notes.filter((note) => tieTypesOf(note.element).has('start'));
  const continuationsOpeningTheLine = drafts.flatMap((draft) =>
    draft.kind === 'fromLineStart' ? [draft.to] : [],
  );

  return drafts.map((draft): LineBreakTieArc => {
    if (draft.kind === 'fromLineStart') {
      return {
        kind: 'fromLineStart',
        to: arcNoteOf(draft.to),
        direction: arcDirection(draft.to, 'stop', continuationsOpeningTheLine),
      };
    }
    const direction = arcDirection(draft.from, 'start', tieStarts);
    return draft.kind === 'toLineEnd'
      ? { kind: 'toLineEnd', from: arcNoteOf(draft.from), direction }
      : { kind: 'withinLine', from: arcNoteOf(draft.from), to: arcNoteOf(draft.to), direction };
  });
}

/** A drawn note OSMD laid out, with the measure it is drawn in. */
type DrawnNote = { note: VexFlowGraphicalNote; measure: VexFlowMeasure };

function drawnNotesOf(graphicSheet: GraphicalMusicSheet): DrawnNote[] {
  return graphicSheet.MeasureList.flat()
    .filter((measure): measure is VexFlowMeasure => measure instanceof VexFlowMeasure)
    .flatMap((measure) =>
      measure.staffEntries.flatMap((staffEntry) =>
        staffEntry.graphicalVoiceEntries.flatMap((voiceEntry) =>
          voiceEntry.notes
            .filter(
              (note: GraphicalNote): note is VexFlowGraphicalNote =>
                note instanceof VexFlowGraphicalNote,
            )
            .map((note) => ({ note, measure })),
        ),
      ),
    );
}

function isDrawnTieNote(drawn: DrawnNote, written: TieArcNote): boolean {
  const source = drawn.note.sourceNote;
  return (
    !source.isRest() &&
    source.PrintObject &&
    !source.ParentVoiceEntry.IsGrace &&
    source.Pitch !== undefined &&
    source.ParentStaff.idInMusicSheet === written.staffIndex &&
    source.Pitch.getHalfTone() + 12 === written.midiPitch &&
    Math.abs(source.getAbsoluteTimestamp().RealValue - written.onset) < ONSET_TOLERANCE &&
    Array.isArray(drawn.note.vfnote) &&
    drawn.note.vfnote[0] !== undefined
  );
}

type StaveTieNotes = ConstructorParameters<typeof VF.StaveTie>[0];
type DirectedStaveTie = VF.StaveTie & { setDirection(direction: number): VF.StaveTie };

const canSetDirection = (tie: VF.StaveTie): tie is DirectedStaveTie => 'setDirection' in tie;

/** A VexFlow tie for these notes, curving as asked (VexFlow: -1 up, +1 down). */
function staveTie(notes: StaveTieNotes, direction: TieArcDirection): VF.StaveTie {
  const tie = new VF.StaveTie(notes);
  if (direction && canSetDirection(tie)) tie.setDirection(direction === 'above' ? -1 : 1);
  return tie;
}

/**
 * The second half of a tie, drawn from the stave's left edge rather than from after
 * the clef: for a line drawn without its clef (the phone's chain of lines keeps the
 * clef's room but shows no clef), so the half meets the first half at the seam.
 */
class TieFromStaveEdge extends VF.StaveTie {
  private readonly arrivingNote: VF.StemmableNote;

  constructor(arrivingNote: VF.StemmableNote, arrivingIndex: number) {
    super({ last_note: arrivingNote, last_indices: [arrivingIndex] });
    this.arrivingNote = arrivingNote;
  }

  renderTie(params: Parameters<VF.StaveTie['renderTie']>[0]): void {
    super.renderTie({ ...params, first_x_px: this.arrivingNote.getStave().getX() });
  }
}

const leavingTowardLineEnd = (from: DrawnNote, direction: TieArcDirection) =>
  staveTie({ first_note: from.note.vfnote[0], first_indices: [from.note.vfnote[1]] }, direction);

function arrivingFromLineStart(
  to: DrawnNote,
  direction: TieArcDirection,
  lineShowsClef: boolean,
): VF.StaveTie {
  if (lineShowsClef) {
    return staveTie({ last_note: to.note.vfnote[0], last_indices: [to.note.vfnote[1]] }, direction);
  }
  const tie = new TieFromStaveEdge(to.note.vfnote[0], to.note.vfnote[1]);
  if (direction && canSetDirection(tie)) tie.setDirection(direction === 'above' ? -1 : 1);
  return tie;
}

/** The ties to draw for one arc, each with the measure that draws it. */
function staveTiesForArc(
  arc: LineBreakTieArc,
  findDrawn: (written: TieArcNote) => DrawnNote | undefined,
  lineShowsClef: boolean,
): Array<{ tie: VF.StaveTie; measure: VexFlowMeasure }> {
  if (arc.kind === 'toLineEnd') {
    const from = findDrawn(arc.from);
    return from ? [{ tie: leavingTowardLineEnd(from, arc.direction), measure: from.measure }] : [];
  }
  if (arc.kind === 'fromLineStart') {
    const to = findDrawn(arc.to);
    return to
      ? [{ tie: arrivingFromLineStart(to, arc.direction, lineShowsClef), measure: to.measure }]
      : [];
  }
  const from = findDrawn(arc.from);
  const to = findDrawn(arc.to);
  if (!from || !to) return [];
  // A tie within one phrase that still breaks across systems splits, as OSMD splits one.
  if (from.measure.ParentStaffLine !== to.measure.ParentStaffLine) {
    return [
      { tie: leavingTowardLineEnd(from, arc.direction), measure: from.measure },
      { tie: arrivingFromLineStart(to, arc.direction, true), measure: to.measure },
    ];
  }
  const tie = staveTie(
    {
      first_note: from.note.vfnote[0],
      first_indices: [from.note.vfnote[1]],
      last_note: to.note.vfnote[0],
      last_indices: [to.note.vfnote[1]],
    },
    arc.direction,
  );
  return [{ tie, measure: to.measure }];
}

/**
 * Add the arcs OSMD misses to a laid-out sheet, so its next draw draws them with
 * its own ties (each measure draws its ties after its notes). Call after each
 * layout: a layout clears the measures' ties. Returns how many ties were added;
 * an arc whose note is not drawn (hidden, or not found) adds none.
 * lineShowsClef: whether the line is drawn with its clef. Without one, a tie's
 * second half starts at the stave's edge instead of after the clef's room.
 */
export function addLineBreakTieArcs(
  graphicSheet: GraphicalMusicSheet,
  arcs: LineBreakTieArc[],
  lineShowsClef: boolean,
): number {
  if (arcs.length === 0) return 0;
  const drawnNotes = drawnNotesOf(graphicSheet);
  const findDrawn = (written: TieArcNote) =>
    drawnNotes.find((drawn) => isDrawnTieNote(drawn, written));
  const staveTies = arcs.flatMap((arc) => staveTiesForArc(arc, findDrawn, lineShowsClef));
  staveTies.forEach(({ tie, measure }) => measure.vfTies.push(tie));
  return staveTies.length;
}

/**
 * Keep the arcs OSMD misses on every layout of this sheet: each render lays the
 * sheet out again (clearing the measures' ties) and then draws it, so the arcs
 * are added between the two. Install once per graphic sheet (OSMD makes a new one
 * on load and on updateGraphic).
 */
export function addLineBreakTieArcsOnEachLayout(
  graphicSheet: GraphicalMusicSheet,
  arcs: LineBreakTieArc[],
  lineShowsClef: boolean,
): void {
  if (arcs.length === 0) return;
  const layOut = graphicSheet.reCalculate.bind(graphicSheet);
  graphicSheet.reCalculate = () => {
    layOut();
    addLineBreakTieArcs(graphicSheet, arcs, lineShowsClef);
  };
}
